import React, { useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import Button from "commons/components/ui/Button";
import Icon from "commons/components/AppIcon";
import useLoginPopup from "module/ai-matchmaking/viewmodal/useLoginPopup";

const LoginPopup = () => {
  const { shouldShowPopup, dismissPopup, navigateToProfile, progress } =
    useLoginPopup();
  const backdropRef = useRef(null);

  // Read hasBeenAYear from sessionStorage for display purposes
  const hasBeenAYear = useMemo(() => {
    try {
      const userData = JSON.parse(sessionStorage.getItem("userData"));
      return userData?.userBusinessProfile?.hasBeenAYear === true;
    } catch {
      return false;
    }
  }, []);

  if (!shouldShowPopup) return null;

  const handleBackdropClick = (e) => {
    if (e.target === backdropRef.current) {
      dismissPopup();
    }
  };

  return ReactDOM.createPortal(
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[9999] overflow-y-auto bg-black/40 backdrop-blur-sm animate-modal-backdrop"
      onMouseDown={handleBackdropClick}
    >
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white w-full sm:max-w-sm rounded-2xl shadow-xl animate-modal-content overflow-hidden">
          {/* Top accent bar */}
          <div className="h-1 w-full bg-logo" />

          {/* Close button */}
          <button
            onClick={dismissPopup}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors p-1.5 hover:bg-gray-100 rounded-full"
          >
            <Icon name="X" size={16} />
          </button>

          {/* Content */}
          <div className="px-6 pt-6 pb-6 flex flex-col items-center text-center gap-4">
            {/* Icon badge */}
            <div className="w-12 h-12 bg-logo/8 border border-logo/20 rounded-xl flex items-center justify-center">
              <Icon name="UserCheck" size={22} className="text-logo" />
            </div>

            {/* Title */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                Complete Your Business Profile
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                Would you like to update your business profile? This helps us
                match you with relevant counterparts.
              </p>
            </div>

            {/* Progress indicator */}
            {progress.completedQuestions > 0 && (
              <div className="w-full bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-2">
                  You've completed {progress.completedQuestions} of{" "}
                  {progress.numberOfQuestions} questions
                </p>
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-logo rounded-full transition-all duration-300"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
              </div>
            )}

            {/* Year reminder */}
            {hasBeenAYear && (
              <p className="text-xs text-logo2 bg-logo2/5 border border-logo2/15 px-3 py-2 rounded-lg w-full">
                It's been a while since you updated — your preferences may have
                changed.
              </p>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 w-full pt-1">
              <Button
                variant="outline"
                className="flex-1"
                onClick={dismissPopup}
              >
                Skip
              </Button>
              <Button
                variant="default"
                className="flex-1"
                onClick={navigateToProfile}
              >
                Update
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LoginPopup;
